import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const STATUSES = ['pending', 'processing', 'completed', 'cancelled'];
const LIMIT = 15;

function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const BLANK_ITEM = { product_id: '', product_name: '', qty: 1, price: '' };

export default function Orders({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [status, setStatus]     = useState('');
  const [search, setSearch]     = useState('');
  const [loading, setLoading]   = useState(false);

  const [modal, setModal]       = useState(false);      // create
  const [editModal, setEditModal] = useState(false);    // edit header only
  const [viewModal, setViewModal] = useState(null);     // view detail

  const [customers, setCustomers] = useState([]);
  const [products, setProducts]   = useState([]);

  // create form
  const [cust, setCust]   = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([{ ...BLANK_ITEM }]);
  const [saving, setSaving] = useState(false);

  // edit form (header only)
  const [editData, setEditData] = useState({});

  useEffect(() => { setTitle('Sales Orders'); }, [setTitle]);

  useEffect(() => {
    api.get('/customers/all').then(r => setCustomers(r.data || [])).catch(() => {});
    api.get('/products/all').then(r => setProducts(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders?status=${status}&search=${encodeURIComponent(search)}&page=${page}&limit=${LIMIT}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [status, search, page]);

  useEffect(() => { load(); }, [load]);

  // ---- line item helpers ----
  const addLine = () => setLineItems(p => [...p, { ...BLANK_ITEM }]);
  const removeLine = (i) => setLineItems(p => p.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setLineItems(p => p.map((it, idx) => {
    if (idx !== i) return it;
    const updated = { ...it, [k]: v };
    if (k === 'product_id') {
      const prod = products.find(x => String(x.id) === String(v));
      if (prod) { updated.product_name = prod.name; updated.price = prod.price; }
    }
    return updated;
  }));

  const lineTotal = lineItems.reduce((s, it) => s + (parseFloat(it.qty || 0) * parseFloat(it.price || 0)), 0);

  // ---- open modals ----
  const openCreate = () => { setCust(''); setNotes(''); setLineItems([{ ...BLANK_ITEM }]); setModal(true); };
  const openView   = async (id) => {
    try { const { data } = await api.get(`/orders/${id}`); setViewModal(data); }
    catch { alert('Could not load order'); }
  };
  const openEdit   = (item) => {
    setEditData({ id: item.id, customer_id: item.customer_id, status: item.status, notes: item.notes || '' });
    setEditModal(true);
  };

  const handleCreate = async () => {
    if (!cust) return alert('Customer is required');
    const validItems = lineItems.filter(it => it.product_id && it.qty > 0 && it.price !== '');
    if (validItems.length === 0) return alert('Add at least one product');
    setSaving(true);
    try {
      await api.post('/orders', { customer_id: cust, notes, items: validItems });
      setModal(false); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error creating order');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/orders/${editData.id}`, { customer_id: editData.customer_id, status: editData.status, notes: editData.notes });
      setEditModal(false); load();
      if (viewModal?.id === editData.id) {
        const { data } = await api.get(`/orders/${editData.id}`); setViewModal(data);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating order');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, num) => {
    if (!window.confirm(`Delete order ${num}? Stock will be restored.`)) return;
    try { await api.delete(`/orders/${id}`); load(); if (viewModal?.id === id) setViewModal(null); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Sales Orders</h1>
        <button className="btn btn-primary" onClick={openCreate}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Order
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search order #, customer…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <select className="form-control" style={{ width: 160 }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} orders</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th className="text-right">Total</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No orders found</td></tr>
                ) : items.map(item => (
                  <tr key={item.id}>
                    <td className="font-mono"><strong>{item.order_number}</strong></td>
                    <td>{item.customer_name || '—'}</td>
                    <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                    <td className="text-muted">{item.created_at ? item.created_at.slice(0, 10) : '—'}</td>
                    <td className="text-right"><strong>{fmtMoney(item.total)}</strong></td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="View" onClick={() => openView(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.order_number)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Create Order Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal modal-xl">
            <div className="modal-header">
              <h2>New Sales Order</h2>
              <button className="modal-close" onClick={() => setModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Customer *</label>
                  <select className="form-control" value={cust} onChange={e => setCust(e.target.value)}>
                    <option value="">Select customer…</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input className="form-control" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" />
                </div>
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Order Items</div>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Product</th>
                      <th style={{ width: '15%' }}>Qty</th>
                      <th style={{ width: '20%' }}>Unit Price</th>
                      <th style={{ width: '20%' }} className="text-right">Subtotal</th>
                      <th style={{ width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((it, i) => (
                      <tr key={i}>
                        <td>
                          <select className="form-control" value={it.product_id} onChange={e => setLine(i, 'product_id', e.target.value)}>
                            <option value="">Select product…</option>
                            {products.map(p => <option key={p.id} value={p.id}>{p.name} (Stock: {p.stock})</option>)}
                          </select>
                        </td>
                        <td>
                          <input type="number" className="form-control" value={it.qty} onChange={e => setLine(i, 'qty', e.target.value)} min="1" />
                        </td>
                        <td>
                          <input type="number" className="form-control" value={it.price} onChange={e => setLine(i, 'price', e.target.value)} step="0.01" min="0" placeholder="0.00" />
                        </td>
                        <td className="text-right">{fmtMoney(parseFloat(it.qty || 0) * parseFloat(it.price || 0))}</td>
                        <td>
                          {lineItems.length > 1 && (
                            <button className="btn-icon danger" onClick={() => removeLine(i)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="items-total">Total: {fmtMoney(lineTotal)}</div>
              </div>
              <button className="btn btn-ghost btn-sm mt-10" onClick={addLine}>+ Add Item</button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating…' : 'Create Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Order Header Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Order</h2>
              <button className="modal-close" onClick={() => setEditModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Customer</label>
                  <select className="form-control" value={editData.customer_id} onChange={e => setEditData(p => ({ ...p, customer_id: e.target.value }))}>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Status</label>
                  <select className="form-control" value={editData.status} onChange={e => setEditData(p => ({ ...p, status: e.target.value }))}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" value={editData.notes} onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes…" />
                </div>
              </div>
              <p className="text-muted mt-10" style={{ fontSize: 12 }}>Note: Line items cannot be edited after creation. Delete and recreate the order to change items.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={saving}>
                {saving ? 'Saving…' : 'Update Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Order Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>Order {viewModal.order_number}</h2>
              <button className="modal-close" onClick={() => setViewModal(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Order #</label><div className="font-mono">{viewModal.order_number}</div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div><span className={`badge badge-${viewModal.status}`}>{viewModal.status}</span></div>
                </div>
                <div className="form-group">
                  <label>Customer</label><div>{viewModal.customer_name}</div>
                </div>
                <div className="form-group">
                  <label>Date</label><div>{viewModal.created_at ? viewModal.created_at.slice(0, 10) : '—'}</div>
                </div>
                {viewModal.notes && (
                  <div className="form-group full">
                    <label>Notes</label><div>{viewModal.notes}</div>
                  </div>
                )}
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>Order Items</div>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th className="text-right">Qty</th>
                      <th className="text-right">Unit Price</th>
                      <th className="text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewModal.items || []).map((it, i) => (
                      <tr key={i}>
                        <td>{it.product_name}</td>
                        <td className="text-right">{it.qty}</td>
                        <td className="text-right">{fmtMoney(it.price)}</td>
                        <td className="text-right">{fmtMoney(parseFloat(it.qty) * parseFloat(it.price))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="items-total">Total: {fmtMoney(viewModal.total)}</div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger btn-sm" onClick={() => { setViewModal(null); handleDelete(viewModal.id, viewModal.order_number); }}>Delete</button>
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => { setViewModal(null); openEdit(viewModal); }}>Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
